/**
 * Simulator Run grades the visual circuit only: Quirk "Retrieve Code" export is wrapped
 * in the harness entry function (e.g. create_circuit) and run with hidden checks.
 *
 * One-time canvas seed on problem open uses editorBodyToQuirkImportSnippet with starterCode only.
 */

const QUANTUM_IMPORT_RE = /^from\s+qiskit\s+import\s+QuantumCircuit\s*(#.*)?$/;
const CIRCUIT_INIT_RE = /^\s*circuit\s*=\s*QuantumCircuit\s*\(/;
const RETURN_STMT_RE = /^\s*return\b/;

/** @param {string} entryName */
function entryDefPattern(entryName) {
  const escaped = entryName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^\\s*def\\s+${escaped}\\s*\\([^)]*\\)\\s*:\\s*(#.*)?$`);
}

/**
 * Lines inside def entryName(...): excluding return statements.
 * @param {string} editorCode
 * @param {string} entryName
 * @returns {string[] | null} null when the entry function is missing
 */
export function extractEntryFunctionBody(editorCode, entryName = 'create_circuit') {
  if (typeof editorCode !== 'string' || editorCode.trim() === '') {
    return null;
  }

  const lines = editorCode.split(/\r?\n/);
  const defRe = entryDefPattern(entryName);
  let defIdx = -1;
  let defIndent = 0;

  for (let i = 0; i < lines.length; i++) {
    if (defRe.test(lines[i])) {
      defIdx = i;
      defIndent = lines[i].search(/\S/);
      break;
    }
  }

  if (defIdx === -1) {
    return null;
  }

  const bodyLines = [];
  for (let i = defIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') {
      bodyLines.push('');
      continue;
    }

    const indent = line.search(/\S/);
    if (indent <= defIndent) {
      break;
    }

    const content = line.slice(indent);
    if (RETURN_STMT_RE.test(content)) {
      continue;
    }
    bodyLines.push(content);
  }

  return bodyLines;
}

/** @param {string} exportedPy */
function indentedBodyLinesFromQuirkExport(exportedPy) {
  const lines = exportedPy.split(/\r?\n/);
  const out = [];
  let consumedHeader = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!consumedHeader) {
      if (trimmed === '' || trimmed.startsWith('#')) continue;
      if (QUANTUM_IMPORT_RE.test(trimmed)) continue;
      consumedHeader = true;
    }
    out.push(line);
  }
  if (out.length === 0) {
    throw new Error('No circuit statements were retrieved from the simulator.');
  }
  return out.map((line) => (line.trim() === '' ? '' : `    ${line}`)).join('\n');
}

/** @param {string} entryName @param {string} exportedPy */
export function wrapQuirkExportAsEntry(entryName, exportedPy) {
  const indented = indentedBodyLinesFromQuirkExport(exportedPy);
  return `def ${entryName}():\n${indented}\n\n    return circuit`;
}

/**
 * Snippet for seeding the visual simulator from the Python editor (preset gates only).
 * @param {string} editorCode
 * @param {string} entryName
 * @returns {string | null}
 */
export function editorBodyToQuirkImportSnippet(editorCode, entryName = 'create_circuit') {
  const body = extractEntryFunctionBody(editorCode, entryName);
  if (!body || body.length === 0) {
    return null;
  }

  const lines = ['from qiskit import QuantumCircuit'];
  let hasInit = false;

  for (const line of body) {
    const trimmed = line.trim();
    if (trimmed === '' || trimmed.startsWith('#')) continue;
    if (RETURN_STMT_RE.test(trimmed)) continue;
    lines.push(trimmed);
    if (CIRCUIT_INIT_RE.test(trimmed)) {
      hasInit = true;
    }
  }

  if (!hasInit) {
    return null;
  }

  return lines.join('\n');
}

/**
 * @param {string} exportedPy Full Qiskit text from Quirk (Retrieve Code generator).
 * @param {{ mode: string, entry?: string, prefix?: string, suffix?: string }} harnessCfg Row from circuitSimulatorHarness.json.
 * @param {{ hiddenHarnessPrefix?: string, hiddenHarnessSuffix?: string }} problem Problem metadata (problems.json).
 */
export function assembleSimulatorRunnable(exportedPy, harnessCfg, problem) {
  if (!harnessCfg || harnessCfg.mode === 'unsupported') {
    throw new Error(
      harnessCfg?.reason ||
        'Simulator Run is not available for this problem. Use Run Code instead.'
    );
  }

  if (harnessCfg.mode === 'hiddenHarness') {
    const entry =
      harnessCfg.entry ||
      (() => {
        throw new Error('Simulator harness missing entry function name.');
      })();
    const pre =
      typeof problem?.hiddenHarnessPrefix === 'string' ? problem.hiddenHarnessPrefix : '';
    const suf =
      typeof problem?.hiddenHarnessSuffix === 'string' ? problem.hiddenHarnessSuffix : '';
    if (pre === '' && suf === '') {
      throw new Error('Problem is missing hidden harness for simulator grading.');
    }
    return `${pre}${wrapQuirkExportAsEntry(entry, exportedPy)}${suf}`;
  }

  if (harnessCfg.mode === 'prefixSuffix') {
    const pre = harnessCfg.prefix || '';
    const suf = harnessCfg.suffix || '';
    const indented = indentedBodyLinesFromQuirkExport(exportedPy);
    return `${pre}${indented}\n\n    return circuit\n${suf}`;
  }

  throw new Error(`Unknown simulator harness mode: ${harnessCfg.mode}`);
}
